import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSocial } from "@/lib/social";
import { api } from "@/lib/api";
import { Users, MessageSquare, Search, UserPlus, Radio, Copy, Check, Ban, VolumeX, Heart, Star, ExternalLink, User } from "lucide-react";

// Small helper that renders an entry inside Following / Subs sections.
function ChannelRow({ item, tag, tagColor, onClose }) {
  return (
    <Link
      to={item.is_live ? `/c/${item.username}/live` : `/c/${item.username}`}
      onClick={onClose}
      data-testid={`dock-channel-${item.username}`}
      className="flex items-center gap-3 px-4 py-2 border-t border-white/5 hover:bg-white/[0.03] transition"
    >
      <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center sc-heading font-bold text-xs relative shrink-0"
           style={{ background: "linear-gradient(135deg, #FF1053, #FF5900)" }}>
        {item.avatar_url ? <img src={item.avatar_url} alt="" className="w-full h-full object-cover" /> : (item.display_name || item.username)[0].toUpperCase()}
        {item.is_live && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#18181C] bg-[color:var(--sc-volt)]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate flex items-center gap-1.5">
          {item.username}
          {item.is_live && <Radio className="w-3 h-3 text-[color:var(--sc-volt)]" />}
        </div>
        <div className="text-[10px] sc-mono text-zinc-500 truncate">
          {item.is_live ? `${(item.viewer_count || 0).toLocaleString()} watching` : (item.display_name || item.username)}
        </div>
      </div>
      {tag && (
        <span className="text-[9px] sc-mono tracking-widest uppercase font-bold" style={{ color: tagColor }}>{tag}</span>
      )}
    </Link>
  );
}

export default function FriendsDock() {
  const { user } = useAuth();
  const social = useSocial();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("friends");   // "friends" | "following" | "subs"
  const [q, setQ] = useState("");
  const [lookup, setLookup] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addMsg, setAddMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [following, setFollowing] = useState([]);
  const [subs, setSubs] = useState([]);
  const searchTimer = useRef();

  // Toggle from the navbar Friends button + Escape to close.
  useEffect(() => {
    const toggle = () => setOpen((v) => !v);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mysticast:toggle-friends", toggle);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mysticast:toggle-friends", toggle);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!q.trim() || q.length < 2) { setLookup([]); return; }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/users/lookup?q=${encodeURIComponent(q.trim())}`);
        setLookup(r.data);
      } catch { setLookup([]); }
    }, 250);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [q]);

  // Load Following + Subs when the dock opens (small enough to refresh each time).
  useEffect(() => {
    if (!open || !user) return;
    api.get("/my/following").then(r => setFollowing(r.data || [])).catch(() => setFollowing([]));
    api.get("/my/channel_subs").then(r => setSubs(r.data || [])).catch(() => setSubs([]));
  }, [open, user]);

  const filteredFriends = useMemo(() => {
    if (!q.trim()) return social.friends;
    const s = q.trim().toLowerCase();
    return social.friends.filter(f =>
      f.username.toLowerCase().includes(s) ||
      (f.user_code || "").toLowerCase().includes(s)
    );
  }, [social.friends, q]);

  const sendReq = async (username) => {
    setBusy(true); setAddMsg("");
    try {
      const r = await social.addFriend(username);
      setAddMsg(r?.status === "accepted" ? `Now friends with ${username}!` : `Request sent to ${username}`);
    } catch (e) {
      setAddMsg(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(user?.user_code || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!user) return null;

  const close = () => setOpen(false);

  return (
    <>
      {/* Dock panel — opens from top-right, anchored below the navbar Friends button */}
      <div
        data-testid="friends-dock-panel"
        className={`fixed top-[64px] right-4 z-40 w-[380px] max-w-[calc(100vw-2rem)] max-h-[70vh] glass rounded-lg border border-white/10 shadow-2xl overflow-hidden flex flex-col transition-all duration-200 origin-top-right ${open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"}`}
      >
        {/* Header: your user code */}
        <div className="p-4 border-b border-white/10 bg-gradient-to-br from-[#FF1053]/10 via-transparent to-[#00F0FF]/10">
          <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>Your MystiCast ID</div>
          <div className="flex items-center justify-between mt-2">
            <div className="sc-mono font-bold text-lg tracking-widest text-white" data-testid="my-user-code">{user.user_code || "—"}</div>
            <button data-testid="copy-user-code" onClick={copyCode} className="p-2 rounded-md border border-white/10 hover:border-[color:var(--sc-volt)] hover:text-[color:var(--sc-volt)] transition">
              {copied ? <Check className="w-4 h-4 text-[color:var(--sc-volt)]" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">Share this code so friends can find you by ID.</div>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 border-b border-white/10 shrink-0">
          {[
            { id: "friends",   label: "Friends",   count: social.friends.length, color: "#FF1053", testId: "dock-tab-friends" },
            { id: "following", label: "Following", count: following.length,     color: "#00E5FF", testId: "dock-tab-following" },
            { id: "subs",      label: "Subs",      count: subs.length,          color: "#CCFF00", testId: "dock-tab-subs" },
          ].map(t => (
            <button
              key={t.id}
              data-testid={t.testId}
              onClick={() => setTab(t.id)}
              className="py-2.5 text-[10px] sc-mono tracking-widest uppercase font-bold transition relative"
              style={{ color: tab === t.id ? t.color : "#9A9AA4" }}
            >
              {t.label} <span className="opacity-60 ml-0.5">{t.count}</span>
              {tab === t.id && (
                <span className="absolute bottom-0 left-1/4 right-1/4 h-[2px]" style={{ background: t.color }} />
              )}
            </button>
          ))}
        </div>

        {/* Search — only shown on Friends tab */}
        {tab === "friends" && (
          <div className="p-3 border-b border-white/10">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input
                data-testid="friends-search-input"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search friends or find by username / ID"
                className="sc-input !pl-9 text-sm"
              />
            </div>
            {addMsg && <div className="text-[11px] text-zinc-400 mt-2">{addMsg}</div>}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {tab === "friends" && (
            <>
              {/* Lookup results */}
              {lookup.length > 0 && (
                <div className="border-b border-white/10">
                  <div className="sc-label px-4 pt-3 pb-2">Search results</div>
                  {lookup.map(u => {
                    const isFriend = social.friends.some(f => f.username === u.username);
                    const isSelf = u.username === user.username;
                    return (
                      <div key={u.id} className="flex items-center gap-3 px-4 py-2 border-t border-white/5">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center sc-heading font-bold text-xs" style={{ background: "linear-gradient(135deg, #FF1053, #FF5900)" }}>
                          {u.username[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{u.username}</div>
                          <div className="text-[10px] sc-mono text-zinc-500">{u.user_code}</div>
                        </div>
                        {isSelf ? (
                          <div className="text-[10px] text-zinc-500 sc-mono uppercase">You</div>
                        ) : isFriend ? (
                          <div className="text-[10px] text-[color:var(--sc-volt)] sc-mono uppercase">Friend</div>
                        ) : (
                          <button data-testid={`lookup-add-${u.username}`} disabled={busy} onClick={() => sendReq(u.username)} className="sc-btn text-[10px] py-1.5 px-3">
                            <UserPlus className="w-3 h-3 inline mr-1" />Add
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Incoming requests */}
              {social.requests.incoming.length > 0 && (
                <div className="border-b border-white/10">
                  <div className="sc-label px-4 pt-3 pb-2" style={{ color: "var(--sc-pink)" }}>Requests · {social.requests.incoming.length}</div>
                  {social.requests.incoming.map(r => (
                    <div key={r.id} className="flex items-center justify-between px-4 py-2 border-t border-white/5">
                      <div className="text-sm">{r.from_username}</div>
                      <div className="flex gap-1">
                        <button data-testid={`dock-accept-${r.from_username}`} onClick={() => social.acceptRequest(r.id)} className="px-2 py-1 rounded-sm bg-[color:var(--sc-volt)] text-black text-[10px] font-bold sc-mono">ACCEPT</button>
                        <button data-testid={`dock-decline-${r.from_username}`} onClick={() => social.declineRequest(r.id)} className="px-2 py-1 rounded-sm border border-white/10 text-zinc-400 text-[10px] font-bold sc-mono">DECLINE</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Friends list */}
              <div>
                <div className="sc-label px-4 pt-3 pb-2">Friends {social.friends.length > 0 && `· ${filteredFriends.length}/${social.friends.length}`}</div>
                {filteredFriends.length === 0 && (
                  <div className="text-xs text-zinc-500 px-4 pb-4">
                    {social.friends.length === 0 ? "No friends yet. Search someone by username or ID above." : "No matches."}
                  </div>
                )}
                {filteredFriends.map(f => (
                  <div key={f.id} className="border-t border-white/5 hover:bg-white/[0.03] transition">
                    {/* Row: avatar + name → primary click opens DM */}
                    <Link
                      to={`/messages?to=${f.username}`}
                      onClick={close}
                      data-testid={`dock-friend-row-${f.username}`}
                      className="flex items-center gap-3 px-4 pt-2.5 pb-1.5"
                    >
                      <div className="w-9 h-9 rounded-full flex items-center justify-center sc-heading font-bold text-sm relative shrink-0" style={{ background: "linear-gradient(135deg, #FF1053, #FF5900)" }}>
                        {f.username[0].toUpperCase()}
                        {f.is_live && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#18181C] bg-[color:var(--sc-volt)]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                          {f.username}
                          {f.is_live && <Radio className="w-3 h-3 text-[color:var(--sc-volt)]" />}
                        </div>
                        <div className="text-[10px] sc-mono text-zinc-500">{f.user_code || ""}</div>
                      </div>
                      <MessageSquare className="w-3.5 h-3.5 text-[color:var(--sc-cyan)]" />
                    </Link>
                    {/* Row action buttons: DM (primary), Profile, Channel/Watch */}
                    <div className="flex items-center gap-1 px-4 pb-2.5">
                      <Link
                        to={`/messages?to=${f.username}`}
                        onClick={close}
                        data-testid={`dock-dm-${f.username}`}
                        className="flex-1 text-center py-1 rounded-sm border border-white/10 text-[10px] sc-mono tracking-widest uppercase font-bold hover:border-[color:var(--sc-cyan)] hover:text-[color:var(--sc-cyan)] transition"
                      >
                        <MessageSquare className="w-3 h-3 inline mr-1" />DM
                      </Link>
                      <Link
                        to={`/u/${f.username}`}
                        onClick={close}
                        data-testid={`dock-profile-${f.username}`}
                        className="flex-1 text-center py-1 rounded-sm border border-white/10 text-[10px] sc-mono tracking-widest uppercase font-bold hover:border-white hover:text-white transition"
                      >
                        <User className="w-3 h-3 inline mr-1" />Profile
                      </Link>
                      <Link
                        to={f.is_live ? `/c/${f.username}/live` : `/c/${f.username}`}
                        onClick={close}
                        data-testid={`dock-channel-btn-${f.username}`}
                        className={`flex-1 text-center py-1 rounded-sm border text-[10px] sc-mono tracking-widest uppercase font-bold transition ${f.is_live ? "border-[color:var(--sc-volt)]/50 text-[color:var(--sc-volt)] hover:bg-[color:var(--sc-volt)]/10" : "border-white/10 hover:border-white hover:text-white"}`}
                      >
                        {f.is_live ? <><Radio className="w-3 h-3 inline mr-1" />Watch</> : <><ExternalLink className="w-3 h-3 inline mr-1" />Channel</>}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "following" && (
            <div data-testid="dock-following-list">
              <div className="sc-label px-4 pt-3 pb-2" style={{ color: "var(--sc-cyan)" }}>Following · {following.length}</div>
              {following.length === 0 && (
                <div className="text-xs text-zinc-500 px-4 pb-4">
                  Not following anyone yet. Follow streamers to see them here — click through to their channel from any browse page.
                </div>
              )}
              {following.map(it => <ChannelRow key={it.username} item={it} tag={it.is_live ? "LIVE" : null} tagColor="var(--sc-volt)" onClose={close} />)}
            </div>
          )}

          {tab === "subs" && (
            <div data-testid="dock-subs-list">
              <div className="sc-label px-4 pt-3 pb-2" style={{ color: "var(--sc-volt)" }}>Active subs · {subs.length}</div>
              {subs.length === 0 && (
                <div className="text-xs text-zinc-500 px-4 pb-4">
                  You have no channel subs. Subscribe from any streamer&apos;s channel for 300 GC / month.
                </div>
              )}
              {subs.map(s => {
                const it = { username: s.channel_owner_username, display_name: s.display_name, avatar_url: s.avatar_url, is_live: s.is_live, viewer_count: s.viewer_count };
                const daysLeft = s.expires_at ? Math.max(0, Math.ceil((new Date(s.expires_at).getTime() - Date.now()) / 86400000)) : 0;
                return <ChannelRow key={s.channel_owner_username} item={it} tag={`${daysLeft}d`} tagColor="var(--sc-volt)" onClose={close} />;
              })}
            </div>
          )}

          {/* Blocked / muted summary (friends tab only) */}
          {tab === "friends" && (social.blocks.length > 0 || social.mutes.length > 0) && (
            <div className="border-t border-white/10 p-3 flex items-center justify-between text-[11px] text-zinc-500">
              <div className="flex items-center gap-3">
                {social.blocks.length > 0 && <span className="flex items-center gap-1"><Ban className="w-3 h-3 text-[color:var(--sc-pink)]" /> {social.blocks.length} blocked</span>}
                {social.mutes.length > 0 && <span className="flex items-center gap-1"><VolumeX className="w-3 h-3 text-[color:var(--sc-orange)]" /> {social.mutes.length} muted</span>}
              </div>
              <Link to="/messages" onClick={close} className="text-[color:var(--sc-cyan)] hover:underline sc-mono">Manage</Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-white/10 flex gap-2">
          <Link data-testid="dock-open-messenger" to="/messages" onClick={close} className="sc-btn-ghost text-xs flex-1 flex items-center justify-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> Messenger
          </Link>
          <Link data-testid="dock-open-parties" to="/parties" onClick={close} className="sc-btn-volt sc-btn text-xs flex-1 flex items-center justify-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Parties
          </Link>
        </div>
      </div>
    </>
  );
}
